import { zodResolver } from "@hookform/resolvers/zod"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import { Card, CardDescription, CardTitle } from "design-system/molecules/Card"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Form } from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useGetWidgetConfigurationQuery } from "modules/campaigns/hooks/sections/widget/useGetWidgetConfigurationQuery"
import { useUpdateWidgetConfigurationQuery } from "modules/campaigns/hooks/sections/widget/useUpdateWidgetConfigurationMutation"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpdateWidgetConfigurationSchema } from "schema"
import { toast } from "sonner"
import z from "zod"
import { WidgetAppearance } from "./boxes/Appearance"
import { BubbleMessages } from "./boxes/BubbleMessages"
import { DisplayUrls } from "./boxes/DisplayUrls"
import { WidgetDomainAndPlacement } from "./boxes/DomainAndPlacement"
import { LanguageOptions } from "./boxes/LanguageOptions"
import { OpeningMessage } from "./boxes/OpeningMessage"
import { QuickActions } from "./boxes/QuickActions"
import { SendBehavior } from "./boxes/SendBehavior"
import { TypingLabel } from "./boxes/TypingLabel"
import { WidgetConfigurationTabs } from "./WidgetConfigurationTabs"
import { WidgetPreview } from "./WidgetPreview"

export type WidgetConfigurationFormType = z.infer<typeof UpdateWidgetConfigurationSchema>

export const WidgetWeb = () => {
  const { t } = useTranslation()
  const [previewLanguage, setPreviewLanguage] = useState<null | string>(null)
  const { isPending } = useGetWidgetConfigurationQuery({
    onSuccess: (data) => {
      form.reset({
        ...data,
        displayMode: data.displayUrls?.length ? "custom" : "all",
      })

      const firstLanguage = data.languages?.at(0)?.value
      if (firstLanguage) {
        setPreviewLanguage(firstLanguage)
      }
    },
  })
  const { mutateAsync, isPending: mutationIsPending } = useUpdateWidgetConfigurationQuery()
  const form = useForm<WidgetConfigurationFormType>({
    resolver: zodResolver(UpdateWidgetConfigurationSchema),
    defaultValues: {
      displayMode: "all",
      displayUrls: [],
      languages: [],
    },
    disabled: mutationIsPending || isPending,
  })

  const onSubmit = (payload: WidgetConfigurationFormType) => {
    mutateAsync({
      ...payload,
      displayUrls: payload.displayMode === "all" ? [] : payload.displayUrls,
    }).then(() => {
      toast.success(t("campaigns.templates.widget.config.saveSuccess"))
    })
  }

  const languages = form.watch("languages")

  const boxes = useMemo(
    () =>
      [
        {
          id: "languageOptions",
          title: t("campaigns.templates.widget.config.languageOptions.title"),
          description: t("campaigns.templates.widget.config.languageOptions.description"),
          element: LanguageOptions,
        },
        {
          id: "bubbleMessages",
          title: t("campaigns.templates.widget.config.bubbleMessages.title"),
          description: t("campaigns.templates.widget.config.bubbleMessages.description"),
          element: BubbleMessages,
        },
        {
          id: "openingMessage",
          title: t("campaigns.templates.widget.config.openingMessage.title"),
          description: t("campaigns.templates.widget.config.openingMessage.description"),
          element: OpeningMessage,
        },
        {
          id: "quickActions",
          title: t("campaigns.templates.widget.config.quickActions.title"),
          description: t("campaigns.templates.widget.config.quickActions.description"),
          element: QuickActions,
        },
        {
          id: "appearance",
          title: t("campaigns.templates.widget.config.appearance.title"),
          description: t("campaigns.templates.widget.config.appearance.description"),
          element: WidgetAppearance,
        },
        {
          id: "typingLabel",
          title: t("campaigns.templates.widget.config.typingLabel.title"),
          description: t("campaigns.templates.widget.config.typingLabel.description"),
          element: TypingLabel,
        },
        {
          id: "sendBehavior",
          title: t("campaigns.templates.widget.config.sendBehavior.title"),
          description: t("campaigns.templates.widget.config.sendBehavior.description"),
          element: SendBehavior,
        },
        {
          id: "displayUrls",
          title: t("campaigns.templates.widget.config.displayUrls.title"),
          description: t("campaigns.templates.widget.config.displayUrls.description"),
          element: DisplayUrls,
        },
        {
          id: "domainAndPlacement",
          title: t("campaigns.templates.widget.config.domainAndPlacement.title"),
          description: t("campaigns.templates.widget.config.domainAndPlacement.description"),
          element: WidgetDomainAndPlacement,
        },
      ] as const,
    [t],
  )

  return (
    <>
      <HeaderButtons identifier="saveWidgetConfiguration">
        <Button disabled={!form.formState.isValid} onClick={form.handleSubmit(onSubmit)}>
          {t("campaigns.templates.openingMessages.form.fields.save")}
        </Button>
      </HeaderButtons>
      <div className="flex flex-col gap-4 h-full">
        <WidgetConfigurationTabs />
        <div className="flex flex-col gap-4 h-full">
          <div className="flex flex-col gap-4 h-full grow">
            <Section
              title={t("campaigns.templates.widget.config.title")}
              description={t("campaigns.templates.widget.config.description")}
            >
              <div className="flex flex-col min-[1440px]:flex-row gap-6">
                <Form
                  {...form}
                  errorTranslations={getErrorTranslations()}
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="flex flex-col gap-4 w-full min-[1440px]:flex-1 min-[1440px]:min-w-0"
                >
                  <Accordion
                    type="multiple"
                    className="w-full flex flex-col gap-4"
                    defaultValue={boxes.slice(0, 1).map((box) => box.id)}
                  >
                    {boxes.map((box) => (
                      <Card key={box.id}>
                        <AccordionItem value={box.id}>
                          <AccordionTrigger>
                            <div className="flex flex-col gap-1">
                              <CardTitle className="text-base">{box.title}</CardTitle>
                              <CardDescription>{box.description}</CardDescription>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="flex flex-col gap-4 text-balance">
                            <box.element form={form} />
                          </AccordionContent>
                        </AccordionItem>
                      </Card>
                    ))}
                  </Accordion>
                </Form>
                <div className="flex flex-col gap-4 h-full w-full min-[1440px]:w-[650px] min-[1440px]:shrink-0">
                  <div className="flex flex-col gap-2 col-span-1 lg:col-span-2">
                    <div className="text-base">
                      {t("campaigns.templates.widget.config.previewLanguage")}
                    </div>
                    <Select value={previewLanguage ?? ""} onValueChange={setPreviewLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages?.map((language) => (
                          <SelectItem key={language.value} value={language.value}>
                            {language.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <WidgetPreview form={form} language={previewLanguage} isLoading={isPending} />
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </>
  )
}
